// How a Shopify product becomes form values. Ported unchanged from public/shopify-prefill.js.
import type { ShopProduct } from '@/entities/shop';
import type { Product } from '@/entities/label';

// Max characters written into the form; longer text is cut at a word boundary.
// (The label itself shortens text further to fit — see fitLabels().)
export const PREFILL_LIMITS = { name: 120, desc: 140 };

// Cut text to `max` characters at a word boundary and end it with "…".
export function _truncate(text: string, max: number){
  text = (text || '').trim();
  if(text.length <= max) return text;
  let cut = text.slice(0, max - 1);
  const space = cut.lastIndexOf(' ');
  if(space > max * 0.6) cut = cut.slice(0, space);
  return cut.replace(/[\s,;:.\-–—]+$/, '') + '…';
}

// Shorten the product title, never the variant name that tells labels apart.
export function _labelName(data: Pick<ShopProduct, 'name' | 'productTitle' | 'variantTitle'>, max: number){
  const variant = (data.variantTitle || '').trim();
  if(!variant) return _truncate(data.name || data.productTitle, max);
  const suffix = ' - ' + variant;
  return _truncate(data.productTitle, Math.max(20, max - suffix.length)) + suffix;
}

// "12.5" → "$12.50"
export function _money(value: string | null){
  const n = parseFloat(value ?? '');
  return isFinite(n) ? '$' + n.toFixed(2) : '';
}

// The fields the lookup fills in; everything else on the label is left alone.
export function filledFields(data: ShopProduct, typedSku: string, current: Product): Partial<Product> {
  const onSale = !!data.compareAtPrice && parseFloat(data.compareAtPrice) > parseFloat(data.price);
  return {
    sku: data.sku || typedSku,
    name: _labelName(data, PREFILL_LIMITS.name),
    desc: _truncate(data.description, PREFILL_LIMITS.desc),
    price: _money(data.price),
    compare: onSale ? _money(data.compareAtPrice) : '',
    qrText: data.url || current.qrText,
  };
}
